import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import NavBar from "../../components/navbar/navbar.component";
import SideBar from "../../components/sidebar/sidebar.component";
import { calculateDeliveryFee, getPriceCities, getPriceCustomers, getPriceTowns } from "./deliveryFeeService";

const locationOptions = (records) => records
  .filter((record) => Number.isInteger(record?.id) && record.id > 0 && typeof record.name === "string" && record.name.trim())
  .map((record) => ({ value: record.id, label: record.name }))
  .sort((first, second) => first.label.localeCompare(second.label));

const feeRows = [
  ["ServiceFee", "Service Fee"],
  ["OnforwardingFee", "Onforwarding Fee"],
  ["Discount", "Discount"],
  ["VatAmount", "VAT"],
  ["TotalAmount", "Total Amount"],
];
const formatAmount = new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" });

export const PriceResult = ({ value }) => {
  return (
    <div className="table-responsive">
      <Table aria-label="Calculated price details">
        <TableHead><TableRow><TableCell>Charge</TableCell><TableCell align="right">Amount (NGN)</TableCell></TableRow></TableHead>
        <TableBody>{feeRows.map(([key, label]) => (
          <TableRow hover key={key}>
            <TableCell component="th" scope="row" sx={{ fontWeight: key === "TotalAmount" ? 700 : 400 }}>{label}</TableCell>
            <TableCell align="right" sx={{ fontWeight: key === "TotalAmount" ? 700 : 400 }}>{formatAmount.format(value[key])}</TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </div>
  );
};

const GeneratePrice = () => {
  const [customers, setCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [towns, setTowns] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [town, setTown] = useState(null);
  const [weight, setWeight] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingTowns, setLoadingTowns] = useState(false);
  const [lookupErrors, setLookupErrors] = useState([]);
  const [townError, setTownError] = useState("");
  const [lookupRetry, setLookupRetry] = useState(0);
  const [townRetry, setTownRetry] = useState(0);
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const submission = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingOptions(true);
    setLookupErrors([]);
    Promise.allSettled([getPriceCustomers(controller.signal), getPriceCities(controller.signal)]).then(([customerResult, cityResult]) => {
      if (controller.signal.aborted) return;
      const failures = [];
      if (customerResult.status === "fulfilled") {
        setCustomers(customerResult.value.filter((record) => Number.isInteger(record?.id) && record.id > 0).map((record) => {
          const name = [record.firstName, record.lastName].filter(Boolean).join(" ");
          return { value: record.id, label: record.companyName ? `${name} (${record.companyName})`.trim() : name || `Customer ${record.id}` };
        }).sort((first, second) => first.label.localeCompare(second.label)));
      } else failures.push(customerResult.reason.message);
      if (cityResult.status === "fulfilled") setCities(locationOptions(cityResult.value));
      else failures.push(cityResult.reason.message);
      setLookupErrors(failures);
      setLoadingOptions(false);
    });
    return () => controller.abort();
  }, [lookupRetry]);

  const destinationId = destination?.value;
  useEffect(() => {
    if (!destinationId) return undefined;
    const controller = new AbortController();
    setLoadingTowns(true);
    setTownError("");
    getPriceTowns(destinationId, controller.signal).then((records) => {
      if (!controller.signal.aborted) setTowns(locationOptions(records));
    }).catch((error) => {
      if (!controller.signal.aborted) setTownError(error.message);
    }).finally(() => {
      if (!controller.signal.aborted) setLoadingTowns(false);
    });
    return () => controller.abort();
  }, [destinationId, townRetry]);

  useEffect(() => () => submission.current?.abort(), []);

  const clearResult = () => {
    setResult(null);
    setRequestError("");
    setErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submission.current || loadingOptions || loadingTowns || lookupErrors.length || townError) return;
    const nextErrors = {};
    if (!customer) nextErrors.customer = "Select a customer.";
    if (!origin) nextErrors.origin = "Select an origin city.";
    if (!destination) nextErrors.destination = "Select a destination city.";
    if (!town) nextErrors.town = "Select a destination town.";
    if (!weight.trim() || !Number.isFinite(Number(weight)) || Number(weight) <= 0) nextErrors.weight = "Enter a weight greater than zero.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const controller = new AbortController();
    submission.current = controller;
    setIsSubmitting(true);
    setRequestError("");
    setResult(null);
    try {
      const data = await calculateDeliveryFee({ customerId: customer.value, originId: origin.value, destinationId: destination.value, townId: town.value, weight }, controller.signal);
      if (!controller.signal.aborted) setResult({ data });
    } catch (error) {
      if (!controller.signal.aborted) setRequestError(error.message || "Unable to generate price. Please try again.");
    } finally {
      if (!controller.signal.aborted) setIsSubmitting(false);
      submission.current = null;
    }
  };

  const selectField = (name, label, options, value, onChange, extra = {}) => (
    <div className="col-md-8">
      <div className="d-flex mb-2">
        <label htmlFor={`price-${name}`} style={{ fontSize: "14px" }} className="col-5 col-md-4 col-lg-3 mb-0">{label}:</label>
        <Select
          className="col-7 col-md-8 col-lg-9 px-0"
          inputId={`price-${name}`}
          options={options}
          value={value}
          isSearchable
          isClearable
          isDisabled={isSubmitting || loadingOptions}
          isLoading={loadingOptions}
          onChange={(selected) => { clearResult(); onChange(selected); }}
          placeholder={name === "customer" ? "Select customer" : `Select ${label.toLowerCase()}`}
          aria-invalid={Boolean(errors[name])}
          aria-required="true"
          aria-describedby={errors[name] ? `price-${name}-error` : undefined}
          {...extra}
        />
      </div>
      {errors[name] && <div id={`price-${name}-error`} className="text-danger small mb-2">{errors[name]}</div>}
    </div>
  );

  return (
    <>
      <NavBar />
      <SideBar />
      <div className="content-page">
        <div className="container-fluid">
          <div className="row"><div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between"><div className="header-title"><h4 className="card-title">Generate Price</h4></div></div>
              <div className="card-body">
                {lookupErrors.length > 0 && <div className="alert alert-danger" role="alert">{lookupErrors.join(" ")} <button type="button" className="btn btn-sm btn-outline-danger ml-2" disabled={loadingOptions} onClick={() => setLookupRetry((value) => value + 1)}>Retry</button></div>}
                <form onSubmit={handleSubmit} noValidate>
                  <div className="row">
                    {selectField("customer", "Select Customer", customers, customer, setCustomer)}
                    <div className="col-md-4 d-flex justify-content-end justify-content-md-start">
                      <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting || loadingOptions || loadingTowns || lookupErrors.length > 0 || Boolean(townError)}>{isSubmitting ? "Generating price…" : "Generate Price"}</button>
                    </div>
                  </div>
                  <div className="row">
                    {selectField("origin", "Origin City", cities, origin, setOrigin)}
                  </div>
                  <div className="row">
                    {selectField("destination", "Destination City", cities, destination, (selected) => {
                      if (selected?.value === destination?.value) return;
                      setDestination(selected);
                      setTown(null);
                      setTowns([]);
                      setTownError("");
                      setLoadingTowns(Boolean(selected));
                    })}
                  </div>
                  <div className="row">
                    {selectField("town", "Destination Town", towns, town, setTown, {
                      isDisabled: isSubmitting || loadingOptions || !destination || loadingTowns || Boolean(townError),
                      isLoading: loadingTowns,
                      noOptionsMessage: () => "No towns available for this city",
                    })}
                  </div>
                  <div className="row">
                    <div className="col-md-8">
                      <div className="d-flex mb-2">
                        <label htmlFor="price-weight" style={{ fontSize: "14px" }} className="col-5 col-md-4 col-lg-3 mb-0">Weight (kg):</label>
                        <input id="price-weight" type="number" min="0" step="any" required className={`form-control form-control-sm col-7 col-md-8 col-lg-9${errors.weight ? " is-invalid" : ""}`} value={weight} disabled={isSubmitting} onChange={(event) => { clearResult(); setWeight(event.target.value); }} aria-invalid={Boolean(errors.weight)} aria-describedby={errors.weight ? "price-weight-error" : undefined} />
                      </div>
                      {errors.weight && <div id="price-weight-error" className="text-danger small mb-2">{errors.weight}</div>}
                    </div>
                  </div>
                  {townError && <div className="alert alert-danger" role="alert">{townError} <button type="button" className="btn btn-sm btn-outline-danger ml-2" onClick={() => setTownRetry((value) => value + 1)}>Retry towns</button></div>}
                </form>
                {requestError && <div className="alert alert-danger mt-4 mb-0" role="alert">{requestError}</div>}
                <div className={isSubmitting || result ? "mt-4" : undefined} aria-live="polite" aria-busy={isSubmitting}>
                  {isSubmitting ? <div className="text-center" role="status"><div className="spinner-border text-danger"><span className="sr-only">Generating price…</span></div></div> : result ? <><h5 className="mb-3">Calculated Price</h5><PriceResult value={result.data} /></> : null}
                </div>
              </div>
            </div>
          </div></div>
        </div>
      </div>
    </>
  );
};

export default GeneratePrice;
